import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that displays how much a customer saves on the selected variant.
 *
 * The savings figure is variant-specific, so it is re-rendered from the section HTML that
 * the variant picker already fetches rather than being recalculated here — Liquid stays the
 * single source of truth for money formatting, which keeps currency, locale and the
 * merchant's display settings correct without duplicating that logic in JS.
 */
class ProductSavings extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event by re-rendering the savings figure.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newSavings = html.querySelector('product-savings');
        if (!newSavings) return;

        // A variant with no markdown renders an empty, hidden element. `childrenOnly` would
        // swap the contents but leave the previous variant's visibility behind, so the host's
        // own display value has to be carried across too.
        this.style.display = newSavings instanceof HTMLElement ? newSavings.style.display : '';

        morph(this, newSavings, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-savings] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-savings')) {
  customElements.define('product-savings', ProductSavings);
}
